/** Catering Packages — pure reducer (no side effects, no UI imports). */
import { CPK } from './cateringPackagesTypes';
import type { CateringPackagesState } from './cateringPackagesTypes';
import type { CateringPackagesAction } from './cateringPackagesActions';
import type { CateringPackage } from '../../components/catering/data/types';

const initialState: CateringPackagesState = {
  packages: [],
  loading: false,
  loaded: false,
  loadedLocationId: null,
  error: null,
  saving: false,
  savingError: null,
  savingErrorCode: null,
  savingErrorStatus: null,
  savingErrorDetails: null,
  reorderError: null,
  lastSavedPackageId: null,
  imageError: null,
  detail: null,
  detailId: null,
  detailLoading: false,
  detailError: null,
  detailNotFound: false,
  catalogItems: [],
  catalogLoading: false,
  catalogLoaded: false,
  catalogLocationId: null,
  catalogError: null,
};

function upsertPackage(list: CateringPackage[], pkg: CateringPackage): CateringPackage[] {
  return list.some((p) => p.id === pkg.id) ? list.map((p) => (p.id === pkg.id ? pkg : p)) : [...list, pkg];
}

const startSave = (state: CateringPackagesState): CateringPackagesState => ({
  ...state,
  saving: true,
  savingError: null,
  savingErrorCode: null,
  savingErrorStatus: null,
  savingErrorDetails: null,
  lastSavedPackageId: null,
  imageError: null,
});

export function cateringPackagesReducer(
  state: CateringPackagesState = initialState,
  action: CateringPackagesAction,
): CateringPackagesState {
  switch (action.type) {
    // ── Fetch ──
    case CPK.FETCH_REQUEST:
      return action.payload.silent ? state : { ...state, loading: true, error: null };
    case CPK.FETCH_SUCCESS:
      return {
        ...state,
        packages: action.payload.packages,
        loading: false,
        loaded: true,
        loadedLocationId: action.payload.locationId,
        error: null,
      };
    case CPK.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    // ── Details: one package from GET /packages/:id ──
    case CPK.DETAIL_FETCH_REQUEST:
      // Always start from nothing: the screen must show fresh data, not whatever copy happens to be in memory.
      return { ...state, detailId: action.payload.id, detail: null, detailLoading: true, detailError: null, detailNotFound: false };
    case CPK.DETAIL_FETCH_SUCCESS: {
      const pkg = action.payload.package;
      if (state.detailId !== pkg.id) return state; // a late answer for a package the screen has since left
      return {
        ...state,
        detail: pkg,
        detailLoading: false,
        detailError: null,
        detailNotFound: false,
        // The list is only refreshed in place (never appended to): it may not even be loaded.
        packages: state.packages.some((p) => p.id === pkg.id) ? upsertPackage(state.packages, pkg) : state.packages,
      };
    }
    case CPK.DETAIL_FETCH_FAILURE:
      if (state.detailId !== action.payload.id) return state;
      return {
        ...state,
        detail: null,
        detailLoading: false,
        detailError: action.payload.message,
        detailNotFound: action.payload.notFound === true,
      };

    // ── Package item picker (catalogue items) ──
    case CPK.CATALOG_FETCH_REQUEST:
      return { ...state, catalogLoading: true, catalogError: null };
    case CPK.CATALOG_FETCH_SUCCESS:
      return {
        ...state,
        catalogItems: action.payload.items,
        catalogLoading: false,
        catalogLoaded: true,
        catalogLocationId: action.payload.locationId,
        catalogError: null,
      };
    case CPK.CATALOG_FETCH_FAILURE:
      return { ...state, catalogLoading: false, catalogError: action.payload.error };

    // ── Save requests (create / update / duplicate) ──
    case CPK.PACKAGE_CREATE_REQUEST:
    case CPK.PACKAGE_UPDATE_REQUEST:
    case CPK.PACKAGE_DUPLICATE_REQUEST:
      return startSave(state);

    // ── Optimistic delete: remove now, the saga refetches on failure ──
    case CPK.PACKAGE_DELETE_REQUEST:
      return {
        ...startSave(state),
        packages: state.packages.filter((p) => p.id !== action.payload.id),
        ...(state.detailId === action.payload.id ? { detail: null, detailId: null } : {}),
      };

    // ── Optimistic reorder: renumber the list 0..n-1 in the new order ──
    case CPK.REORDER_REQUEST: {
      const order = new Map(action.payload.orderedIds.map((id, i) => [id, i] as const));
      const now = new Date().toISOString();
      const moved = state.packages.map((p) => {
        const next = order.get(p.id);
        if (next === undefined || p.sortOrder === next) return p;
        return { ...p, sortOrder: next, updatedAt: now };
      });
      return {
        ...state,
        reorderError: null,
        // Keep the list in display order so screens can render it as-is.
        packages: [...moved].sort((a, b) => (a.sortOrder ?? Number.POSITIVE_INFINITY) - (b.sortOrder ?? Number.POSITIVE_INFINITY)),
      };
    }
    case CPK.REORDER_SUCCESS:
      return state;
    case CPK.REORDER_FAILURE:
      return { ...state, reorderError: action.payload.message };

    // ── Successes ──
    case CPK.PACKAGE_CREATE_SUCCESS:
    case CPK.PACKAGE_UPDATE_SUCCESS:
      return {
        ...state,
        saving: false,
        packages: upsertPackage(state.packages, action.payload.package),
        lastSavedPackageId: action.payload.package.id,
        imageError: action.payload.imageError ?? null,
        // POST / PATCH return the same package object as GET /:id, so the open details screen needs no re-fetch.
        ...(state.detailId === action.payload.package.id ? { detail: action.payload.package } : {}),
      };
    case CPK.PACKAGE_DUPLICATE_SUCCESS:
      return {
        ...state,
        saving: false,
        packages: upsertPackage(state.packages, action.payload.package),
        lastSavedPackageId: action.payload.package.id,
      };
    case CPK.PACKAGE_DELETE_SUCCESS:
      return { ...state, saving: false };

    // ── Failures ──
    case CPK.PACKAGE_CREATE_FAILURE:
    case CPK.PACKAGE_UPDATE_FAILURE:
    case CPK.PACKAGE_DUPLICATE_FAILURE:
    case CPK.PACKAGE_DELETE_FAILURE:
      return {
        ...state,
        saving: false,
        savingError: action.payload.message,
        savingErrorCode: action.payload.code ?? null,
        savingErrorStatus: action.payload.status ?? null,
        savingErrorDetails: action.payload.details ?? null,
      };

    case CPK.CLEAR_SAVE_STATE:
      return {
        ...state,
        saving: false,
        savingError: null,
        savingErrorCode: null,
        savingErrorStatus: null,
        savingErrorDetails: null,
        reorderError: null,
        lastSavedPackageId: null,
        imageError: null,
      };

    default:
      return state;
  }
}
